/*
  ReplyTemplatesManager — full CRUD for reply templates.

  Rendered as a Settings sub-page (not a modal) so managers can browse
  their whole snippet library, edit tags for smarter per-service
  matching, and delete stale entries without opening the Draft Reply
  dialog on every change.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, Copy, Loader2, Save, MessageSquare, Mail, Search, Tag,
} from "lucide-react";

const EMPTY = { name: "", channel: "email", subject: "", body: "", tags: [], is_shared: true };

export default function ReplyTemplatesManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template obj (with `__new` flag) or null
  const [filter, setFilter] = useState({ channel: "all", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reply-templates");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/reply-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function duplicate(t) {
    try {
      await api.post("/reply-templates", {
        name: `${t.name} (copy)`,
        channel: t.channel,
        subject: t.subject || "",
        body: t.body,
        tags: t.tags || [],
        is_shared: t.is_shared,
      });
      toast.success("Duplicated");
      load();
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const filtered = useMemo(() => {
    const q = (filter.q || "").toLowerCase().trim();
    return rows.filter((t) => {
      if (filter.channel !== "all" && t.channel !== filter.channel) return false;
      if (!q) return true;
      return (
        (t.name || "").toLowerCase().includes(q) ||
        (t.body || "").toLowerCase().includes(q) ||
        (t.tags || []).some((tag) => tag.toLowerCase().includes(q))
      );
    });
  }, [rows, filter]);

  return (
    <div className="max-w-[1200px] mx-auto p-4 space-y-4" data-testid="reply-templates-page">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Settings
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-950">Reply templates</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Save common SMS and email replies for one-click use in the Draft AI Reply dialog.
            Tag templates with a service name (e.g. <code>exercise-physiology</code>) so they auto-rank first for matching enquiries.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY, __new: true })}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="template-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> New template
        </Button>
      </div>

      <Card className="p-4 border-zinc-200">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-2.5" />
            <Input
              placeholder="Search name, body or tag…"
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
              className="pl-8 text-sm"
              data-testid="template-search"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            {["all", "sms", "email"].map((c) => (
              <button
                key={c}
                onClick={() => setFilter((f) => ({ ...f, channel: c }))}
                className={`px-2.5 py-1 rounded-md border ${
                  filter.channel === c
                    ? "bg-zinc-950 text-white border-zinc-950"
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                }`}
                data-testid={`template-filter-${c}`}
              >
                {c === "all" ? "All" : c.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-12 border border-dashed border-zinc-300 rounded-md px-4 text-center">
          {rows.length === 0 ? "No templates yet. Click New template to add one." : "No templates match this filter."}
        </div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((t) => (
            <li key={t.id} data-testid={`template-row-${t.id}`}>
              <Card className="p-4 border-zinc-200 space-y-2 h-full">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-zinc-950 truncate">{t.name}</div>
                      <span
                        className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                          t.channel === "sms"
                            ? "bg-blue-50 text-blue-800 border-blue-200"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200"
                        }`}
                      >
                        {t.channel === "sms" ? <MessageSquare className="h-2.5 w-2.5 inline mr-0.5" /> : <Mail className="h-2.5 w-2.5 inline mr-0.5" />}
                        {t.channel}
                      </span>
                      {t.seeded && (
                        <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">
                          Seeded
                        </span>
                      )}
                      {!t.is_shared && (
                        <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                          Personal
                        </span>
                      )}
                    </div>
                    {t.channel === "email" && t.subject && (
                      <div className="text-2xs text-zinc-500 mt-0.5">
                        Subject: {t.subject}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => duplicate(t)}
                      data-testid={`template-duplicate-${t.id}`}
                      title="Duplicate"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(t)}
                      data-testid={`template-edit-${t.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(t)}
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      data-testid={`template-delete-${t.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-zinc-700 whitespace-pre-wrap line-clamp-3 border border-zinc-100 rounded bg-zinc-50 p-2">
                  {t.body}
                </div>
                {(t.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {(t.tags || []).map((tag) => (
                      <span key={tag} className="text-2xs px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 inline-flex items-center gap-0.5">
                        <Tag className="h-2.5 w-2.5" /> {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <TemplateEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function TemplateEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name: initial.name || "",
    channel: initial.channel || "email",
    subject: initial.subject || "",
    body: initial.body || "",
    tags: (initial.tags || []).join(", "),
    is_shared: initial.is_shared ?? true,
  });
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.body.trim()) { toast.error("Body is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        channel: form.channel,
        subject: form.channel === "email" ? form.subject.trim() : "",
        body: form.body,
        tags: form.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
        is_shared: !!form.is_shared,
      };
      if (isNew) {
        await api.post("/reply-templates", payload);
        toast.success("Template created");
      } else {
        await api.put(`/reply-templates/${initial.id}`, payload);
        toast.success("Template saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New template" : "Edit template"}</DialogTitle>
          <DialogDescription>
            Use <code className="px-1 rounded bg-zinc-100">{"{{first_name}}"}</code> or <code className="px-1 rounded bg-zinc-100">{"{{name}}"}</code> to insert the enquirer&apos;s name.
            Tags help the Draft Reply picker rank this template first for matching enquiries.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Name*</Label>
            <Input
              value={form.name}
              onChange={change("name")}
              placeholder="e.g. Pricing enquiry"
              className="mt-1"
              data-testid="template-name-input"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Channel</Label>
            <div className="mt-1 flex gap-1">
              {["sms", "email"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, channel: c })}
                  className={`text-xs px-3 py-1.5 rounded-md border ${
                    form.channel === c
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "bg-white text-zinc-700 border-zinc-300"
                  }`}
                  data-testid={`template-channel-${c}`}
                >
                  {c === "sms" ? <MessageSquare className="h-3.5 w-3.5 mr-1 inline" /> : <Mail className="h-3.5 w-3.5 mr-1 inline" />}
                  {c.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Visibility</Label>
            <div className="mt-1">
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!form.is_shared}
                  onChange={(e) => setForm({ ...form, is_shared: e.target.checked })}
                  data-testid="template-is-shared"
                />
                Share with the whole team
              </label>
            </div>
          </div>
          {form.channel === "email" && (
            <div className="sm:col-span-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Email subject</Label>
              <Input
                value={form.subject}
                onChange={change("subject")}
                placeholder="Re: Our pricing"
                className="mt-1"
                data-testid="template-subject-input"
              />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Body* <span className="normal-case text-zinc-400 text-2xs">— supports {"{{first_name}}"} and {"{{name}}"}</span>
            </Label>
            <Textarea
              value={form.body}
              onChange={change("body")}
              rows={form.channel === "sms" ? 4 : 10}
              className="mt-1 font-mono-num text-sm"
              placeholder={form.channel === "sms"
                ? "Hi {{first_name}}, thanks for your enquiry…"
                : "Hi {{first_name}},\n\nThanks for reaching out about…"}
              data-testid="template-body-input"
            />
            {form.channel === "sms" && (
              <div className="text-2xs text-zinc-500 mt-1">
                Length: {form.body.length} chars (aim for under 320)
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <Tag className="h-3 w-3" /> Tags <span className="normal-case text-zinc-400 text-2xs">— comma-separated</span>
            </Label>
            <Input
              value={form.tags}
              onChange={change("tags")}
              placeholder="pricing, exercise-physiology, waitlist"
              className="mt-1 font-mono-num text-xs"
              data-testid="template-tags-input"
            />
            <p className="text-2xs text-zinc-500 mt-1">
              Match your <b>Service of interest</b> values (e.g. <code>exercise-physiology</code>) to make this template auto-rank first when replying to matching enquiries.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="template-save-btn"
          >
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
              : <><Save className="h-4 w-4 mr-1" /> Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
